import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import SiteFooter from "@/components/SiteFooter";
import { useAuth } from "@/context/AuthContext";
import { CardTile, money, shortMoney, useStoreCart } from "@/lib/giftStore";
import { ArrowLeft, Minus, Plus, Trash2, Gift, Wallet, CreditCard, Mail, Loader2, ShieldCheck, Lock, Copy, Check, ShoppingCart } from "lucide-react";

const METHODS = [
    { id: "wallet", label: "GiftToCash wallet", note: "Instant. Uses your balance.", icon: Wallet },
    { id: "card", label: "Credit or debit card", note: "Secure checkout by Stripe.", icon: CreditCard },
    { id: "paypal", label: "PayPal", note: "Pay with your PayPal account.", icon: CreditCard },
    { id: "etransfer", label: "Interac e-Transfer", note: "We deliver once the money arrives.", icon: Mail },
];

function Steps({ step }) {
    const items = ["Cart", "Pay", "Get your code"];
    return (
        <ol className="flex items-center gap-2 text-xs font-semibold">
            {items.map((t, i) => (
                <li key={t} className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center ${i <= step ? "bg-emerald-500 text-emerald-950" : "bg-white/10 text-slate-400"}`}>{i + 1}</span>
                    <span className={i <= step ? "text-white" : "text-slate-400"}>{t}</span>
                    {i < items.length - 1 && <span className="w-6 h-px bg-white/20" />}
                </li>
            ))}
        </ol>
    );
}

function CopyRow({ label, value }) {
    const [ok, setOk] = useState(false);
    return (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-white ring-1 ring-sky-100 px-4 py-3">
            <div className="min-w-0"><div className="text-xs text-slate-500">{label}</div><div className="font-bold text-slate-900 truncate">{value}</div></div>
            <button onClick={() => { navigator.clipboard?.writeText(String(value)); setOk(true); setTimeout(() => setOk(false), 1500); }}
                aria-label={`Copy ${label}`} className="w-9 h-9 rounded-lg hover:bg-sky-50 flex items-center justify-center text-sky-700">{ok ? <Check size={16} /> : <Copy size={16} />}</button>
        </div>
    );
}

export default function CartPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { user } = useAuth();
    const { cart, setQty, clear } = useStoreCart();
    const initial = params.get("cur") === "USD" || params.get("cur") === "CAD" ? params.get("cur") : (cart.CAD.length || !cart.USD.length ? "CAD" : "USD");
    const [cur, setCur] = useState(initial);
    const [cfg, setCfg] = useState(null);
    const [balances, setBalances] = useState(null);
    const [method, setMethod] = useState("");
    const [giftOn, setGiftOn] = useState(false);
    const [gift, setGift] = useState({ to_email: "", to_name: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [etr, setEtr] = useState(null);

    const [live, setLive] = useState({});
    const items = useMemo(() => (cart[cur] || []).map((x) => {
        const d = (live[x.product_id]?.denominations || []).find((dd) => Number(dd.value) === Number(x.value));
        return d ? { ...x, price: d.price } : x;
    }), [cart, cur, live]);
    const total = useMemo(() => Math.round(items.reduce((s, x) => s + x.price * x.qty, 0) * 100) / 100, [items]);
    const face = useMemo(() => items.reduce((s, x) => s + x.value * x.qty, 0), [items]);
    const balance = balances ? Number(balances[cur] || 0) : null;
    const needKyc = !!(user && cfg?.require_kyc && user.kyc_status !== "approved" && user.role !== "admin" && user.role !== "staff");

    useEffect(() => {
        api.get(`/store/catalog?country=${cur === "USD" ? "US" : "CA"}`).then((r) => setCfg(r.data)).catch(() => setCfg({ methods: {}, open: true }));
    }, [cur]);
    useEffect(() => {
        if (!user) return;
        api.get("/wallet").then((r) => setBalances(r.data?.balances || null)).catch(() => {});
    }, [user]);
    useEffect(() => {
        const c = params.get("cancelled");
        if (c && user) {
            api.post(`/store/orders/${c}/cancel`).catch(() => {});
            toast("Payment cancelled. Your cart is still here.");
        }
    }, [params, user]);

    const enabled = METHODS.filter((m) => cfg?.methods?.[m.id]);
    useEffect(() => {
        if (!enabled.length) return;
        if (!method || !enabled.some((m) => m.id === method)) {
            const w = enabled.find((m) => m.id === "wallet");
            setMethod(w && balance != null && balance >= total ? "wallet" : (enabled.find((m) => m.id !== "wallet") || enabled[0]).id);
        }
    }, [cfg, balance, total]); // eslint-disable-line react-hooks/exhaustive-deps

    // keep cart prices fresh from the catalog
    useEffect(() => {
        const m = {};
        (cfg?.products || []).forEach((p) => { m[p.id] = p; });
        setLive(m);
    }, [cfg]);
    const problems = items.map((x) => {
        const p = live[x.product_id];
        if (!cfg?.products) return null;
        if (!p) return "No longer for sale";
        const d = (p.denominations || []).find((dd) => Number(dd.value) === Number(x.value));
        if (d && !d.available) return "Sold out";
        if (!d && !p.custom) return "This amount isn't available";
        return null;
    });

    const placeOrder = async () => {
        setErr("");
        if (!user) { navigate(`/login?redirect=${encodeURIComponent(`/buy-giftcard/cart?cur=${cur}`)}`); return; }
        if (!items.length || !method) return;
        if (giftOn && !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(gift.to_email.trim())) { setErr("Enter your friend's email."); return; }
        setBusy(true);
        try {
            const r = await api.post("/store/checkout", {
                currency: cur, method,
                items: items.map((x) => ({ product_id: x.product_id, value: x.value, qty: x.qty })),
                gift: giftOn ? gift : null,
            });
            const d = r.data;
            api.post("/track/buygiftcard", { event: "checkout_" + method }).catch(() => {});
            if (d.next === "redirect" && d.url) { window.location.href = d.url; return; }
            clear(cur);
            if (d.next === "etransfer") { setEtr(d.order); return; }
            navigate(`/dashboard/gift-card-orders?order=${d.order.id}`);
        } catch (e) {
            setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#f4f7f6]">
            <PublicNav />
            <section className="bg-[#070b14] text-white">
                <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <Link to="/buy-giftcard" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ArrowLeft size={16} /> Keep shopping</Link>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>{etr ? "Almost done" : "Checkout"}</h1>
                    </div>
                    <Steps step={etr ? 1 : 0} />
                </div>
            </section>

            <main className="max-w-6xl mx-auto px-4 py-8">
                {etr ? (
                    <div className="max-w-xl mx-auto rounded-3xl bg-white ring-1 ring-slate-200 p-6 sm:p-8">
                        <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center"><Mail size={22} /></div>
                        <h2 className="mt-4 text-xl font-bold text-slate-900">Send your Interac e-Transfer</h2>
                        <p className="mt-1 text-sm text-slate-500">Your order is saved. We deliver the code as soon as the money arrives. We also emailed you these steps.</p>
                        <div className="mt-5 space-y-2 rounded-2xl bg-sky-50/60 p-3">
                            <CopyRow label="Send to" value={etr.etransfer?.email} />
                            <CopyRow label="Amount" value={money(etr.currency, etr.total)} />
                            <CopyRow label="Message (required)" value={etr.id} />
                        </div>
                        <div className="mt-6 flex flex-col sm:flex-row gap-2">
                            <Link to={`/dashboard/gift-card-orders?order=${etr.id}`} className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center">Track my order</Link>
                            <Link to="/buy-giftcard" className="flex-1 h-12 rounded-xl ring-1 ring-slate-200 font-bold text-slate-800 flex items-center justify-center">Back to store</Link>
                        </div>
                    </div>
                ) : (
                    <>
                        {(cart.CAD.length > 0 && cart.USD.length > 0) && (
                            <div className="mb-5 inline-flex rounded-2xl bg-white p-1 ring-1 ring-slate-200">
                                {["CAD", "USD"].map((c) => (
                                    <button key={c} onClick={() => setCur(c)} className={`px-4 h-10 rounded-xl text-sm font-bold ${cur === c ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                                        {c === "CAD" ? "Canada" : "USA"} cart · {cart[c].reduce((s, x) => s + x.qty, 0)}
                                    </button>
                                ))}
                            </div>
                        )}
                        {items.length === 0 ? (
                            <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-12 text-center">
                                <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center"><ShoppingCart size={24} /></div>
                                <h2 className="mt-4 text-xl font-bold text-slate-900">Your {cur === "CAD" ? "Canada" : "USA"} cart is empty</h2>
                                <Link to="/buy-giftcard" className="mt-5 inline-flex h-11 px-5 rounded-xl bg-emerald-600 text-white font-bold items-center">Browse gift cards</Link>
                            </div>
                        ) : (
                            <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
                                <div className="space-y-4 min-w-0">
                                    <div className="rounded-3xl bg-white ring-1 ring-slate-200 divide-y divide-slate-100">
                                        {items.map((x, i) => (
                                            <div key={`${x.product_id}-${x.value}`} className="flex items-center gap-4 p-4">
                                                <div className="w-24 shrink-0"><CardTile brand={x.brand} name={x.name} color={x.color} image={x.image_url} size="sm" value={x.value} cur={cur} /></div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-slate-900 truncate">{x.name}</div>
                                                    <div className="text-xs text-slate-500">{shortMoney(cur, x.value)} card{x.price !== x.value ? ` · you pay ${shortMoney(cur, x.price)}` : ""}</div>
                                                    {problems[i] && <div className="mt-1 text-xs font-semibold text-rose-600">{problems[i]}</div>}
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <div className="flex items-center rounded-full ring-1 ring-slate-200">
                                                            <button aria-label="Less" onClick={() => setQty(cur, i, x.qty - 1)} className="w-8 h-8 flex items-center justify-center"><Minus size={13} /></button>
                                                            <span className="w-6 text-center text-sm font-bold">{x.qty}</span>
                                                            <button aria-label="More" onClick={() => setQty(cur, i, x.qty + 1)} className="w-8 h-8 flex items-center justify-center"><Plus size={13} /></button>
                                                        </div>
                                                        <button onClick={() => setQty(cur, i, 0)} aria-label="Remove" className="w-8 h-8 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                                <div className="font-bold text-slate-900 whitespace-nowrap">{money(cur, x.price * x.qty)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-4 sm:p-5">
                                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                                            <span className="flex items-center gap-3">
                                                <span className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center"><Gift size={18} /></span>
                                                <span><span className="block font-semibold text-slate-900">Send as a gift</span><span className="block text-xs text-slate-500">We email the code only to your friend, with your message. It stays private from you too.</span></span>
                                            </span>
                                            <input type="checkbox" checked={giftOn} onChange={(e) => setGiftOn(e.target.checked)} className="w-5 h-5 accent-emerald-600" />
                                        </label>
                                        {giftOn && (
                                            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                                                <input value={gift.to_email} onChange={(e) => setGift({ ...gift, to_email: e.target.value })} type="email" placeholder="Friend's email" aria-label="Friend's email" className="h-11 rounded-xl ring-1 ring-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                                                <input value={gift.to_name} onChange={(e) => setGift({ ...gift, to_name: e.target.value })} placeholder="Friend's name (optional)" aria-label="Friend's name" maxLength={80} className="h-11 rounded-xl ring-1 ring-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                                                <textarea value={gift.message} onChange={(e) => setGift({ ...gift, message: e.target.value })} placeholder="Message (optional)" aria-label="Gift message" maxLength={300} rows={2} className="sm:col-span-2 rounded-xl ring-1 ring-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <aside className="lg:sticky lg:top-24 space-y-4">
                                    <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-5">
                                        <div className="text-sm font-semibold text-slate-800 mb-3">Pay with</div>
                                        {cfg && enabled.length === 0 && <p className="text-sm text-slate-500">Payments are paused right now.</p>}
                                        <div className="space-y-2" role="radiogroup" aria-label="Payment method">
                                            {enabled.map((m) => {
                                                const low = m.id === "wallet" && balance != null && balance < total;
                                                return (
                                                    <label key={m.id} className={`flex items-center gap-3 rounded-2xl p-3 cursor-pointer ring-1 transition-all ${method === m.id ? "ring-2 ring-emerald-500 bg-emerald-50/50" : "ring-slate-200 hover:ring-slate-300"} ${low ? "opacity-60" : ""}`}>
                                                        <input type="radio" name="pay" checked={method === m.id} onChange={() => setMethod(m.id)} className="w-4 h-4 accent-emerald-600" />
                                                        <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"><m.icon size={17} /></span>
                                                        <span className="flex-1 min-w-0">
                                                            <span className="block text-sm font-semibold text-slate-900">{m.label}</span>
                                                            <span className="block text-xs text-slate-500">{m.id === "wallet" && balance != null ? `Balance ${money(cur, balance)}${low ? " · not enough" : ""}` : m.note}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="rounded-3xl bg-slate-950 text-white p-5">
                                        <div className="flex justify-between text-sm text-slate-300"><span>Card value</span><span>{money(cur, face)}</span></div>
                                        {face - total > 0.004 && <div className="flex justify-between text-sm text-emerald-300 mt-1"><span>You save</span><span>-{money(cur, face - total)}</span></div>}
                                        {total - face > 0.004 && <div className="flex justify-between text-sm text-slate-300 mt-1"><span>Service fee</span><span>{money(cur, total - face)}</span></div>}
                                        <div className="mt-3 pt-3 border-t border-white/10 flex items-end justify-between"><span className="text-sm text-slate-300">Total</span><span className="text-3xl font-extrabold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>{money(cur, total)}</span></div>
                                        {needKyc && (
                                            <div className="mt-3 rounded-xl bg-amber-400/15 text-amber-100 text-sm px-3 py-2.5">
                                                Verify your ID before buying gift cards. It keeps your account and payments safe.
                                                <Link to="/dashboard/kyc" className="mt-2 flex items-center justify-center h-10 rounded-lg bg-amber-300 text-amber-950 font-bold">Verify my ID</Link>
                                            </div>
                                        )}
                                        {err && <div className="mt-3 rounded-xl bg-rose-500/15 text-rose-200 text-sm px-3 py-2">{err}{/KYC/.test(err) && !needKyc ? <> <Link to="/dashboard/kyc" className="underline font-semibold">Verify my ID</Link></> : null}</div>}
                                        <button onClick={placeOrder} disabled={busy || !method || needKyc || (cfg && !cfg.open) || problems.some(Boolean)} data-testid="store-place-order"
                                            className="mt-4 w-full h-13 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-extrabold disabled:opacity-50 flex items-center justify-center gap-2">
                                            {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
                                            {!user ? "Log in to pay" : method === "etransfer" ? "Place order" : `Pay ${money(cur, total)}`}
                                        </button>
                                        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck size={13} /> Prices are checked again on our server when you pay.</p>
                                    </div>
                                </aside>
                            </div>
                        )}
                    </>
                )}
            </main>
            <SiteFooter />
        </div>
    );
}
